import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { USERS_CREATE, USERS_DELETE, USERS_FETCH, USERS_INVITATION_RESEND } from '../action-types';
import { from, mergeMap, switchMap, withLatestFrom } from 'rxjs';
import {
    CreateUserAction,
    DeleteUserAction,
    FetchUsersAction,
    ResendUserInvitationAction,
    SetUserAction,
    SetUsersAction,
    SetUsersFetchingAction,
    UnsetUserAction
} from './users-actions';
import { IAppState } from '../app/app-state';
import { getLocationPreference } from '../preference/preference-selectors';
import { LocationPreference } from '@core/models/preference/location-preference';
import { UserDataService } from '@core/data/api/user/user-data.service';
import { ossNormalize } from '@core/operators/normalize/normalize.operator';
import { USER_ENTITY } from '../entity-types';
import { User } from '@core/models/user/user';
import { NormalizeResponse } from '@core/operators/normalize/normalize-response';
import { ossCatchError } from '@core/operators/catch-error.operator';
import { schema } from 'normalizr';

@Injectable()
export class UsersEffects {

    constructor(private userDataService: UserDataService,
                private store$: Store<IAppState>,
                private actions$: Actions) {
    }

    fetchUsers$ = createEffect(() => 
        this.actions$.pipe(
            ofType<FetchUsersAction>(USERS_FETCH),
            withLatestFrom(
                this.store$.select(getLocationPreference)
            ),
            switchMap(
                ([action, locationPreference]: [FetchUsersAction, LocationPreference]) => {

                    const locationId: string = locationPreference.id;

                    return this.userDataService.fetchIHDUsers(locationId)
                        .pipe(
                            ossNormalize(USER_ENTITY, User, 'users'),
                            mergeMap((normalized: NormalizeResponse) => {

                                return [
                                    new SetUsersAction(normalized),
                                    new SetUsersFetchingAction(false)
                                ];
                            }),
                            ossCatchError(([actions]) =>
                                from(actions.concat([new SetUsersFetchingAction(false)]))
                            )
                        );
                }
            )
        )
    );

    createUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType<CreateUserAction>(USERS_CREATE),
            withLatestFrom(
                this.store$.select(getLocationPreference)
            ),
            switchMap(([action, locationPreference]: [CreateUserAction, LocationPreference]) => {

                return this.userDataService.createIHDUser({
                        ...action.payload.request,
                        location_id: locationPreference.id
                    })
                    .pipe(
                        mergeMap((response) => {
                            return [
                                (action as any).success(response),
                                new SetUserAction({
                                    item: Object.assign(new User(), response.user),
                                    schema: new schema.Entity(USER_ENTITY)
                                })
                                
                            ];
                        }),
                        ossCatchError(([errorActions, error]) =>
                            from(errorActions.concat([(action as any).error(error)]))
                        )
                    );
            })
        )
    );

    resendInvitation$ = createEffect(() =>
        this.actions$.pipe(
            ofType<ResendUserInvitationAction>(USERS_INVITATION_RESEND),
            switchMap((action: ResendUserInvitationAction) => {

                return this.userDataService.resendInvitation(action.payload.userId)
                    .pipe(
                        mergeMap((response) => {
                            return [
                                (action as any).success(response)
                            ];
                        }),
                        ossCatchError(([errorActions, error]) =>
                            from(errorActions.concat([(action as any).error(error)]))
                        )
                    );
            })
        )
    );

    deleteUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType<DeleteUserAction>(USERS_DELETE),
            withLatestFrom(
                this.store$.select(getLocationPreference)
            ),
            switchMap(([action, locationPreference]: [DeleteUserAction, LocationPreference]) => {

                return this.userDataService.deleteIHDUser(action.payload.userId, locationPreference.id)
                    .pipe(
                        mergeMap((response) => {
                            return [
                                (action as any).success(response),
                                new UnsetUserAction({ id: action.payload.userId })
                            ];
                        }),
                        ossCatchError(([errorActions, error]) =>
                            from(errorActions.concat([(action as any).error(error)]))
                        )
                    );
            })
        )
    );
}
